// GA4 loading + consent only. All gtag-calling helpers (trackEvent, trackPageview)
// live in tracking.ts, which owns the single global `window.gtag` declaration.
// nothing loads until the user accepts, IP anonymized, Do Not Track respected.

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined
const CONSENT_KEY = 'metocean-analytics-consent'

let initialized = false

export type ConsentChoice = 'granted' | 'denied'

export function getStoredConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    return v === 'granted' || v === 'denied' ? v : null
  } catch {
    return null
  }
}

export function setStoredConsent(choice: ConsentChoice) {
  try {
    localStorage.setItem(CONSENT_KEY, choice)
  } catch {
    /* storage unavailable — ignore */
  }
}

function dntEnabled(): boolean {
  const dnt =
    navigator.doNotTrack ||
    (window as unknown as { doNotTrack?: string }).doNotTrack
  return dnt === '1' || dnt === 'yes'
}

/** Load GA4 once, after consent. Safe to call repeatedly. */
export function initAnalytics() {
  if (initialized) return
  if (!GA_ID) return
  if (dntEnabled()) return

  initialized = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(script)

  const w = window as unknown as {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
  w.dataLayer = w.dataLayer || []
  w.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments)
  }
  w.gtag('js', new Date())
  w.gtag('config', GA_ID, { anonymize_ip: true, send_page_view: false })
}